import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { object, string } from "zod";

import { Input, Label, IconPencil, Button } from "@busbud/horizon";

import { useAppContext } from "@app/helpers/hooks";
import type { NapiUser } from "@app/types/napi-types";

import type { SubmitHandler } from "react-hook-form";

import { openModal } from "../../../../../store/state/modals";
import { updateUser } from "../../../../../store/state/user";

const email_validation = object({
  email: string().email()
}).optional();

type OwnProps = {
  onSuccess: () => void;
};

type ReduxProps = {
  user: NapiUser;
};

export const EmailSection: React.FC<OwnProps & ReduxProps> = ({
  user,
  onSuccess
}) => {
  const dispatch = useDispatch();
  const { translator } = useAppContext();

  const methods = useForm({
    defaultValues: { email: user.email },
    resolver: zodResolver(email_validation)
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { touchedFields, errors, isDirty, isValid, isSubmitting }
  } = methods;

  const onSubmit: SubmitHandler<any> = values => {
    dispatch(
      openModal({
        type: "prompt_password",
        updated_email: values.email,
        onClosed: () => {},
        onEmailUpdated: (updated_user: NapiUser) => {
          dispatch(updateUser(updated_user));
          reset({ email: updated_user.email });
          onSuccess();
        }
      })
    );
  };

  return (
    <div className="flex flex-col gap-200">
      <div className="flex flex-col gap-100">
        <Label as="span" size="lg" fontWeight="bold">
          {translator.t("!accounts.settings.email-address")}
        </Label>
        <Label as="span" size="md" className="text-color-secondary">
          {translator.t("!accounts.email.usage-description")}
        </Label>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-150">
          <Input
            size="md"
            type="text"
            data-testid="email-field"
            isDisabled={user.signin_method !== "email"}
            {...register("email")}
            labelText={translator.t("!accounts.settings.email-address")}
            isInvalid={!!touchedFields.email && !!errors.email}
            iconEnd={<IconPencil size="md" />}
            className="w-full"
          />
          {!!isDirty && (
            <Button
              id="submit"
              isSubmit
              data-testid="submit-button"
              isDisabled={isSubmitting || !isValid}
              variant="contained"
              size="lg"
              className="w-full"
              appearance="primary"
            >
              {translator.t("!button.save")}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};
